/**
 * Purchase Limit - Clos Selection
 * ---------------------------------
 * Restreint la quantité max par commande sur les produits ayant
 * le metafield custom.max_quantity_per_order défini (Integer).
 *
 * Limite appliquée par PRODUIT (toutes variantes confondues).
 *
 * Points d'interception :
 * 1. Page produit : clic sur le bouton add to cart (avant que Dawn ne soumette)
 * 2. Cart drawer  : clic sur le bouton "+" et changement de quantité
 * 3. Cards de collection : géré directement dans theme.liquid
 *    (qui appelle window.PurchaseLimit.showToast en cas de blocage)
 */

(function () {
  'use strict';

  var TOAST_DURATION = 4000;

  // =========================================================================
  // Helpers
  // =========================================================================

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .catch(function (e) {
        console.error('[PurchaseLimit] Impossible de récupérer le panier', e);
        return { items: [] };
      });
  }

  function getProductQuantityInCart(cart, productId) {
    var id = parseInt(productId, 10);
    return cart.items
      .filter(function (item) { return item.product_id === id; })
      .reduce(function (sum, item) { return sum + item.quantity; }, 0);
  }

  function buildErrorMessage(max, current) {
    if (current >= max) {
      return 'Limite atteinte : maximum ' + max + ' unité' + (max > 1 ? 's' : '') +
             ' de ce produit par commande.';
    }
    var remaining = max - current;
    return 'Limite : ' + max + ' unité' + (max > 1 ? 's' : '') +
           ' max par commande. Il vous reste ' + remaining + ' unité' +
           (remaining > 1 ? 's' : '') + ' disponible' + (remaining > 1 ? 's' : '') + '.';
  }

  function showToast(message) {
    var existing = document.querySelector('.purchase-limit-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'purchase-limit-toast';
    toast.setAttribute('role', 'alert');
    toast.textContent = message;

    var styles = {
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      maxWidth: '360px',
      padding: '14px 18px',
      background: '#33141F',
      color: '#fff',
      fontSize: '14px',
      lineHeight: '1.4',
      borderRadius: '4px',
      boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
      zIndex: '9999',
      opacity: '0',
      transition: 'opacity 0.25s ease',
      fontFamily: 'var(--font-body-family, sans-serif)'
    };
    Object.keys(styles).forEach(function (k) { toast.style[k] = styles[k]; });

    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.style.opacity = '1'; });

    setTimeout(function () {
      toast.style.opacity = '0';
      setTimeout(function () { toast.remove(); }, 300);
    }, TOAST_DURATION);
  }

  // Expose pour le handler dans theme.liquid (cards de collection)
  window.PurchaseLimit = {
    showToast: showToast,
    getCart: getCart,
    getProductQuantityInCart: getProductQuantityInCart
  };

  // =========================================================================
  // 1. Page produit
  //
  // Stratégie : on intercepte le clic sur le bouton add-to-cart en phase capture.
  // - 1er clic utilisateur : on bloque + on vérifie le cart en async
  //   - si OK : on pose un flag et on re-déclenche le clic ; le handler natif
  //     de Dawn (sur le form submit) prendra le relais
  //   - si dépassement : on affiche le toast et on s'arrête
  // - 2e clic (re-déclenché par nous) : flag présent → on laisse passer
  // =========================================================================

  function handleProductFormClick(e) {
    var btn = e.target.closest('product-form button[type="submit"][name="add"]');
    if (!btn) return;

    var productId = btn.dataset.productId;
    var maxQuantity = parseInt(btn.dataset.maxQuantity, 10);

    // Pas de restriction sur ce produit → on laisse passer
    if (!productId || !maxQuantity || isNaN(maxQuantity)) return;

    // Re-clic programmatique après vérif OK → on laisse passer
    if (btn.dataset.purchaseLimitChecked === 'true') {
      delete btn.dataset.purchaseLimitChecked;
      return;
    }

    // 1er clic → on bloque le temps de vérifier
    e.preventDefault();
    e.stopImmediatePropagation();

    var form = btn.closest('form');
    var qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    var quantityToAdd = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

    getCart().then(function (cart) {
      var current = getProductQuantityInCart(cart, productId);

      if (current + quantityToAdd > maxQuantity) {
        showToast(buildErrorMessage(maxQuantity, current));
        // Restaurer l'état visuel du bouton si Dawn l'avait passé en loading
        btn.classList.remove('loading');
        btn.removeAttribute('aria-disabled');
        var spinner = btn.querySelector('.loading__spinner, .loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      } else {
        // OK : on pose le flag et on re-clique → le 2e passage laisse passer,
        // puis Dawn fait son add-to-cart natif
        btn.dataset.purchaseLimitChecked = 'true';
        btn.click();
      }
    });
  }

  // =========================================================================
  // 2. Cart drawer — interception du bouton "+" et de la saisie quantité
  // =========================================================================

  function handleCartDrawerPlusClick(e) {
    var plusBtn = e.target.closest('cart-drawer button.quantity__button[name="plus"]');
    if (!plusBtn) return;

    var itemRow = plusBtn.closest('.cart-item[data-max-quantity]');
    if (!itemRow) return;

    // Flag pour re-clic OK
    if (plusBtn.dataset.purchaseLimitChecked === 'true') {
      delete plusBtn.dataset.purchaseLimitChecked;
      return;
    }

    var productId = itemRow.dataset.productId;
    var maxQuantity = parseInt(itemRow.dataset.maxQuantity, 10);
    if (!productId || !maxQuantity || isNaN(maxQuantity)) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    getCart().then(function (cart) {
      var current = getProductQuantityInCart(cart, productId);
      if (current + 1 > maxQuantity) {
        showToast(buildErrorMessage(maxQuantity, current));
      } else {
        plusBtn.dataset.purchaseLimitChecked = 'true';
        plusBtn.click();
      }
    });
  }

  function handleCartDrawerQtyChange(e) {
    var input = e.target.closest('cart-drawer input.quantity__input');
    if (!input) return;
    if (input.dataset.purchaseLimitProgrammatic === 'true') return;

    var itemRow = input.closest('.cart-item[data-max-quantity]');
    if (!itemRow) return;

    var productId = itemRow.dataset.productId;
    var maxQuantity = parseInt(itemRow.dataset.maxQuantity, 10);
    if (!productId || !maxQuantity || isNaN(maxQuantity)) return;

    var newQty = parseInt(input.value, 10) || 0;
    var lineKey = itemRow.dataset.lineKey;

    getCart().then(function (cart) {
      var currentTotal = getProductQuantityInCart(cart, productId);
      var currentLine = cart.items.find(function (i) { return i.key === lineKey; });
      var currentLineQty = currentLine ? currentLine.quantity : 0;
      var projectedTotal = currentTotal - currentLineQty + newQty;

      if (projectedTotal > maxQuantity) {
        var maxForThisLine = Math.max(0, maxQuantity - (currentTotal - currentLineQty));
        input.dataset.purchaseLimitProgrammatic = 'true';
        input.value = maxForThisLine;
        var ev = new Event('change', { bubbles: true });
        input.dispatchEvent(ev);
        delete input.dataset.purchaseLimitProgrammatic;
        showToast('Limite : ' + maxQuantity + ' unité' + (maxQuantity > 1 ? 's' : '') +
                  ' max par commande pour ce produit.');
      }
    });
  }

  // =========================================================================
  // Init : listeners delegate sur document en capture phase
  // =========================================================================

  function init() {
    document.addEventListener('click', handleProductFormClick, true);
    document.addEventListener('click', handleCartDrawerPlusClick, true);
    document.addEventListener('change', handleCartDrawerQtyChange, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
